import { User as Auth0User, useAuth0 } from '@auth0/auth0-react';
import { useParams } from '@tanstack/react-router';
import { useMemo } from 'react';

import { useGetMeQuery } from '@/api/api-hooks';
import { UserRoleItem, userRoleItems } from '@/components/User/userRoleItems';

interface User {
  userName: string;
  userId: string;
  email: string;
  entitlements: string[];
  featureFlags: string[];
  isMeLoading: boolean;
  isDriverAiUser: boolean | undefined;
  user?: Auth0User;
  organizationId: string | null;
  organizationName: string | null;
  organizationDisplayName: string | null;
  organizationLogoUrl: string | null;
  is404: boolean;
  isAuthenticated: boolean;
  isSelfService: boolean;
  orgRole?: UserRoleItem;
  isSuperAdmin: boolean;
  isSourceAdmin: boolean;
  isTeamAdmin: boolean;
  /**
   * Returns the organizationName from the URL instead of Auth0 and defaults to empty if it's not present.
   * This avoids having to wait for Auth0 to load. The 404 gate should take over in the case where
   * routeOrganizationName doesn't match organizationName.
   */
  routeOrganizationName: string;
}

export const isDriverEmail = (email: string): boolean => {
  const driverAiDomains = ['@driverai.com', '@driver.ai'];
  return driverAiDomains.some((domain) => email.includes(domain));
};

export const useUser = (): User => {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const { data: meData, isLoading: isMeLoading } = useGetMeQuery({
    enabled: isAuthenticated,
    refetchInterval: 1000 * 60 * 5, // 5 minutes
    staleTime: 1000 * 60 * 5, // data stays fresh for 5 minutes
  });
  const actualIsMeLoading = !isAuthenticated || isMeLoading;
  const isDriverAiUser = isDriverEmail(user?.email ?? '');
  const { organizationName: routeOrganizationName } = useParams({ strict: false });

  const userName: string = user?.name || '';
  const email: string = user?.email || '';
  const userId: string = user?.sub || '';
  const organizationId = user?.org_id ?? null;
  const organizationName = user?.org_name ?? null;
  const organizationDisplayName = user?.org_display_name ?? null;
  const organizationLogoUrl = user?.org_logo_url ?? null;
  const isSelfService = user?.self_service === true;

  const is404 = !!organizationName && !!routeOrganizationName && organizationName !== routeOrganizationName;

  const orgRole = useMemo(() => userRoleItems.find((item) => item.value === meData?.org_role), [meData?.org_role]);

  return {
    userName,
    userId,
    email,
    entitlements: meData?.entitlements ?? [],
    featureFlags: meData?.feature_flags ?? [],
    isMeLoading: isLoading || actualIsMeLoading,
    isDriverAiUser,
    user,
    organizationId,
    organizationName,
    organizationDisplayName,
    organizationLogoUrl,
    is404,
    isAuthenticated,
    isSelfService,
    orgRole,
    isSuperAdmin: meData?.org_role === 'org_super_admin',
    isSourceAdmin: meData?.source_admin ?? false,
    isTeamAdmin: meData?.team_admin ?? false,
    routeOrganizationName: routeOrganizationName ?? '',
  };
};
